import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";

import moment from "moment";
import {
  authorizationResource,
  currentUserResource,
  paymentResource,
  pricingTableResource,
  registrationResource,
} from "./api/resources";
import { EMPTY_PARTICIPANT } from "./config/constants";
import { i18n } from "./plugins/i18n";

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  key: "registration-page",
});

Vue.use(Vuex);
const defaultRegistration = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    registration: {
      contact: {
        salutation: null,
        firstname: null,
        lastname: null,
        title: null,
        companyAddition: null,
        department: null,
        phone: null,
        email: null,
      },
      invoice: {
        firstname: null,
        lastname: null,
        companyAddition: null,
        department: null,
        street: null,
        zip: null,
        city: null,
        country: null,
        phone: null,
        email: null,
        vatId: null,
        combinedInvoice: null,
        notes: null,
      },
      payment: {},
      participants: [
        {
          ...EMPTY_PARTICIPANT,
          ...(urlParams.has("booth")
            ? { type: "booth", boothNumber: urlParams.get("booth") }
            : {}),
        },
      ],
    },
  };
};

export default new Vuex.Store({
  state: {
    currentUser: null,
    pageType: null,
    registration: defaultRegistration().registration,

    pricingTable: {
      none: {
        kolloquium: 0,
        kolloquiumTunneldays: 0,
        sightseeing: 0,
        dinner: 0,
        excursion: 0,
      },
    },

    loading: false,
  },
  mutations: {
    addParticipant(state) {
      // deep copy also the selected packages array
      const newParticipant = JSON.parse(JSON.stringify(EMPTY_PARTICIPANT));
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("booth")) {
        newParticipant.type = "booth";
        newParticipant.boothNumber = urlParams.get("booth");
      }
      state.registration.participants.push(newParticipant);
    },

    removeParticipant(state, index) {
      state.registration.participants.splice(index, 1);
    },

    clearParticipantFiles(state) {
      state.registration.participants.forEach((entry) => {
        entry.documentLocation = null;
        entry.documentCertificateLocation = null;
      });
    },

    setCurrentUser(state, currentUser) {
      state.currentUser = currentUser;
    },

    setPageType(state, pageType) {
      state.pageType = pageType;
    },

    setPrice(state, prices) {
      state.pricingTable = prices;
    },

    updateContactData(state) {
      state.registration.contact = {
        ...state.registration.contact,
        ...state.registration.participants[0],
      };
    },

    updateInvoiceData(state) {
      state.registration.invoice = {
        ...state.registration.invoice,
        ...state.registration.contact,
      };
    },

    resetRegistration(state) {
      state.registration = defaultRegistration().registration;
    },

    setPayment(state, payment) {
      state.registration.payment = payment;
    },

    toggleSelectedPackage(state, payload) {
      const index = state.registration.participants[
        payload.index
      ].selectedPackages.findIndex((p) => p.key === payload.key);

      if (index !== -1) {
        const dependantPrice = state.pricingTable.find(
          (entry) =>
            entry._dependantBy &&
            entry._dependantBy.slug ===
              state.registration.participants[payload.index].selectedPackages[
                index
              ].key
        );
        state.registration.participants[payload.index].selectedPackages.splice(
          index,
          1
        );
        // is there a selected dependant price --> when yes, unselect!
        if (dependantPrice) {
          const dependantIndex = state.registration.participants[
            payload.index
          ].selectedPackages.findIndex(
            (entry) => entry.key === dependantPrice.slug
          );
          if (dependantIndex >= 0) {
            state.registration.participants[
              payload.index
            ].selectedPackages.splice(dependantIndex, 1);
          }
        }
      } else {
        state.registration.participants[payload.index].selectedPackages.push(
          payload
        );
      }

      Vue.set(
        state.registration.participants,
        payload.index,
        state.registration.participants[payload.index]
      );
    },

    updateSelectedPackage(state, payload) {
      const index = state.registration.participants[
        payload.index
      ].selectedPackages.findIndex((p) => p.key === payload.key);
      if (index !== -1) {
        state.registration.participants[payload.index].selectedPackages[
          index
        ] = payload;
      } else {
        state.registration.participants[payload.index].selectedPackages.push(
          payload
        );
      }
      Vue.set(
        state.registration.participants,
        payload.index,
        state.registration.participants[payload.index]
      );
    },

    toggleLoading(state, payload) {
      state.loading = payload;
    },

    setParticipantShowInFolder(state, payload) {
      state.registration.participants[payload.index].showInFolder =
        payload.value;
    },

    setParticipantVirtualOnly(state, payload) {
      state.registration.participants[payload.index].virtualOnly =
        payload.value;
    },

    setCombinedInvoice(state, payload) {
      state.registration.invoice.combinedInvoice = payload.value;
    },

    updateCountry(state, payload) {
      state.registration.invoice.country = payload;
    },
  },
  actions: {
    updateInvoiceData({ commit }) {
      commit("updateInvoiceData");
    },
    updateContactData({ commit }) {
      commit("updateContactData");
    },
    addParticipant({ commit }) {
      commit("addParticipant");
    },
    removeParticipant({ commit }, index) {
      commit("removeParticipant", index);
    },
    clearParticipantFiles({ commit }) {
      commit("clearParticipantFiles");
    },

    setPageType({ commit }, pageType) {
      commit("setPageType", pageType);
    },

    async getCurrentUser({ commit }) {
      let currentUser = null;
      await currentUserResource
        .get()
        .then((response) => (currentUser = response.data))
        .catch(() => (currentUser = null));
      commit("setCurrentUser", currentUser);
      return currentUser;
    },

    // eslint-disable-next-line
    async getPrice({ commit }, type) {
      const price = await pricingTableResource.get();
      commit(
        "setPrice",
        price.data.results.sort((a, b) =>
          a.mandatory === b.mandatory ? 0 : a.mandatory ? -1 : 1
        )
      );
      return price.data;
    },

    resetRegistration() {
      this.commit("resetRegistration");
    },

    async charge({ state }, source) {
      return (
        await paymentResource.post(
          `/charge/${source}`,
          {
            registration: state.registration,
          },
          {
            headers: {
              "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
            },
          }
        )
      ).data;
    },

    async saveRegistration({ state }) {
      try {
        const res = await registrationResource.post(
          "",
          { pageId: window.pageId, ...state.registration },
          {
            headers: {
              "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
            },
          }
        );
        return res.data;
      } catch (err) {
        return {
          err: true,
        };
      }
    },

    async authorize({ state }) {
      try {
        const payment = (
          await authorizationResource.post(
            "",
            {
              registration: state.registration,
            },
            {
              headers: {
                "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
              },
            }
          )
        ).data;

        return payment;
      } catch (err) {
        return {
          err: true,
        };
      }
    },

    authorizeBankTransfer({ commit }) {
      commit("setPayment", {
        method: "banktransfer",
      });
    },

    toggleSelectedPackage({ commit }, payload) {
      commit("toggleSelectedPackage", payload);
    },

    updateSelectedPackage({ commit }, payload) {
      commit("updateSelectedPackage", payload);
    },
    toggleLoading({ commit }, payload) {
      commit("toggleLoading", payload);
    },
  },
  getters: {
    currentUser: (state) => state.currentUser,
    contact: (state) => state.registration.contact,
    invoice: (state) => state.registration.invoice,
    participants: (state) => state.registration.participants,
    pricingTable: (state) => state.pricingTable,
    registration: (state) => state.registration,
    cartSum: (state, getters) => {
      return state.registration.participants
        .map((participant) => getters.participantCost(participant))
        .reduce((left, right) => left + right);
    },
    participantCost: (state, getters) => (participant) => {
      if (!participant || participant.selectedPackages.length === 0) {
        return 0;
      }
      const filteredPackages = getters.participanFilteredSelectedPackages(
        participant
      );
      if (filteredPackages.length === 0) {
        return 0;
      }
      return filteredPackages
        .map((pkg) => {
          return getters.participantPackagePrice(pkg, participant);
        })
        .reduce((a, b) => a + b);
    },
    participanFilteredSelectedPackages: (state) => (participant) => {
      return participant.selectedPackages.filter((entry) => {
        const priceEntry = state.pricingTable.find(
          (table) => table.slug === entry.key
        );
        if (!priceEntry) {
          return false;
        }
        return (
          (participant.virtualOnly && priceEntry.virtual) ||
          (!participant.virtualOnly && !priceEntry.virtual)
        );
      });
    },
    // eslint-disable-next-line
    participantSelection: (state, getters) => (index, key) => {
      const isSelected = state.registration.participants[
        index
      ].selectedPackages.find((pkg) => pkg.key === key);
      return isSelected || { value: "" };
    },
    priceForKey: (state) => (key) =>
      state.pricingTable.find((table) => table.slug === key) || false,

    // eslint-disable-next-line
    todaysPrice: (state) => (prices) => {
      const ret = prices.filter(
        (price) =>
          !price.startDate ||
          !price.endDate ||
          (moment(price.startDate).isBefore(moment()) &&
            moment().isBefore(moment(price.endDate).add(1, "d")))
      );
      return ret[0];
    },

    participantPackagePrice: (state, getters) => (pkg, participant) => {
      let type = participant.type || "none";
      if (type === "booth") {
        type = "none";
      }
      if (
        pkg.key.includes("speaker") ||
        pkg.key.includes("multiplePersons") ||
        pkg.key.includes("multiplePersonsWithName")
      ) {
        return 0;
      }

      const pricingTable = state.pricingTable.find((p) => pkg.key === p.slug);
      let speakerTypeOverride =
        (pricingTable.priceType === "speaker" ||
          pricingTable.priceType === "webinar") &&
        participant.selectedPackages.find(
          (p) => p.key === pkg.key + "-speaker"
        ) &&
        state.pageType !== "ISRM"
          ? "speaker"
          : type;
      let factor = 1;

      if (pricingTable.priceType === "multiplePersons") {
        const additionInfo = participant.selectedPackages.find(
          (p) => p.key === pkg.key + "-multiplePersons"
        );
        if (additionInfo) {
          factor = parseInt(additionInfo.value);
        }
      } else if (pricingTable.priceType === "multiplePersonsWithName") {
        const additionInfo = participant.selectedPackages.find(
          (p) => p.key === pkg.key + "-multiplePersonsWithName"
        );
        if (additionInfo) {
          factor = parseInt(additionInfo.value.split(",").length);
        }
      }
      return (
        getters.todaysPrice(pricingTable[speakerTypeOverride].prices).price *
        factor
      );
    },

    additionalInfoForPrice: (state) => (pkg, price, participant) => {
      let factor = 1;
      let info = "";
      if (
        price.priceType.includes("speaker") ||
        price.priceType.includes("multiplePersons") ||
        price.priceType.includes("multiplePersonsWithName")
      ) {
        const additionalInfo = participant.selectedPackages.find(
          (p) => p.key === pkg.key + "-" + price.priceType
        );
        const pricingTable = state.pricingTable.find((p) => pkg.key === p.slug);
        if (additionalInfo) {
          info = additionalInfo.value;
          if (pricingTable.priceType === "multiplePersons") {
            factor = parseInt(additionalInfo.value);
            info = `${i18n.t("form.labels.multiplePersons")}: ${info}`;
          } else if (pricingTable.priceType === "multiplePersonsWithName") {
            factor = parseInt(additionalInfo.value.split(",").length);
            info = `${i18n.t("form.labels.multiplePersonsWithName")}: ${info}`;
          } else if (pricingTable.priceType === "speaker") {
            info = `${i18n.t("form.labels.speaker")}`;
          }
        }
      }
      return {
        factor,
        info,
      };
    },

    pricingTableForPackage: (state) => (pckg) => {
      return state.pricingTable.find((price) => price.slug === pckg) || false;
    },

    // eslint-disable-next-line
    priceTitle: (state) => (price, lang) => {
      return lang === "de"
        ? price.title
        : lang === "es"
        ? price.title_es || price.title_en
        : price.title_en;
    },

    // eslint-disable-next-line
    priceInfoText: (state) => (price, lang) => {
      return lang === "de"
        ? price.text
        : lang === "es"
        ? price.text_es || price.text_en
        : price.text_en;
    },

    loading: (state) => state.loading,
  },

  plugins: [vuexLocal.plugin],
});
